/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

// Header scrolling hide/show
document.addEventListener("DOMContentLoaded", () => {
  const wrapper = document.querySelector(
    ".shopify-section.shopify-section-group-header-group.shopify-section__header",
  );
  const header = wrapper ? wrapper.querySelector(".header") : null;
  const navBar = wrapper ? wrapper.querySelector(".nav-bar") : null;
  const desktopQuery = window.matchMedia("(min-width: 1000px)");
  const stateClasses = [
    "custom-header--at-top",
    "custom-header--nav-only",
    "custom-header--full",
  ];
  const scrollThreshold = 100;
  const deltaThreshold = 5;
  let lastScrollY = window.scrollY;
  let currentState = "";

  if (!wrapper || !header || !navBar) return;

  function setHeaderState(state) {
    if (currentState === state) return;

    wrapper.classList.remove(...stateClasses);
    wrapper.classList.add(state);
    currentState = state;
  }

  function updateHeaderMainHeight() {
    wrapper.style.setProperty(
      "--custom-header-main-height",
      `${header.offsetHeight}px`,
    );
  }

  function resetHeaderState() {
    wrapper.classList.remove(...stateClasses);
    wrapper.style.removeProperty("--custom-header-main-height");
    currentState = "";
  }

  function syncHeaderState() {
    updateHeaderMainHeight();

    if (!desktopQuery.matches) {
      resetHeaderState();
      lastScrollY = window.scrollY;
      return;
    }

    const currentScrollY = window.scrollY;
    const scrollDelta = Math.abs(currentScrollY - lastScrollY);

    if (currentScrollY <= 0) {
      setHeaderState("custom-header--at-top");
    } else if (currentState === "" && currentScrollY > scrollThreshold) {
      setHeaderState("custom-header--nav-only");
    } else if (currentState === "") {
      setHeaderState("custom-header--full");
    } else if (currentScrollY < lastScrollY && scrollDelta > deltaThreshold) {
      setHeaderState("custom-header--full");
    } else if (
      currentScrollY > lastScrollY &&
      currentScrollY > scrollThreshold &&
      scrollDelta > deltaThreshold
    ) {
      setHeaderState("custom-header--nav-only");
    }

    lastScrollY = currentScrollY;
  }

  updateHeaderMainHeight();
  syncHeaderState();

  window.addEventListener("scroll", syncHeaderState, { passive: true });
  window.addEventListener("resize", syncHeaderState);

  if (desktopQuery.addEventListener) {
    desktopQuery.addEventListener("change", syncHeaderState);
  } else if (desktopQuery.addListener) {
    desktopQuery.addListener(syncHeaderState);
  }
});

// Keep the phone-sized cart drawer inside the actually visible viewport. The
// theme's built-in calculation uses the combined header height, which can be
// taller or shorter than the header portion currently visible on screen.
document.addEventListener("DOMContentLoaded", () => {
  const miniCart = document.getElementById("mini-cart");
  const phoneViewport = window.matchMedia("(max-width: 640px)");
  let layoutSyncQueued = false;

  if (!miniCart) return;

  function syncMiniCartViewport() {
    layoutSyncQueued = false;

    // The cart lives inside the header's stacking context, while app launchers
    // such as Shopify Forms and Smile Rewards are attached at document level.
    // Expose the open state so CSS can lift the whole header context temporarily.
    document.documentElement.classList.toggle(
      "is-mini-cart-open",
      miniCart.getAttribute("aria-hidden") === "false",
    );

    if (!phoneViewport.matches) {
      miniCart.style.removeProperty("--mini-cart-available-height");
      return;
    }

    const visualViewport = window.visualViewport;
    const viewportTop = visualViewport ? visualViewport.offsetTop : 0;
    const viewportBottom = visualViewport
      ? visualViewport.offsetTop + visualViewport.height
      : window.innerHeight;
    const miniCartTop = Math.max(
      miniCart.getBoundingClientRect().top,
      viewportTop,
    );
    const availableHeight = Math.max(
      Math.floor(viewportBottom - miniCartTop),
      0,
    );

    miniCart.style.setProperty(
      "--mini-cart-available-height",
      `${availableHeight}px`,
    );
  }

  function queueMiniCartViewportSync() {
    if (layoutSyncQueued) return;

    layoutSyncQueued = true;
    window.requestAnimationFrame(syncMiniCartViewport);
  }

  const miniCartLayoutObserver = new MutationObserver(
    queueMiniCartViewportSync,
  );

  miniCartLayoutObserver.observe(miniCart, {
    attributes: true,
    attributeFilter: ["aria-hidden"],
    childList: true,
    subtree: true,
  });

  window.addEventListener("resize", queueMiniCartViewportSync);
  document.addEventListener(
    "cart:rerendered",
    queueMiniCartViewportSync,
    true,
  );

  if (window.visualViewport) {
    window.visualViewport.addEventListener(
      "resize",
      queueMiniCartViewportSync,
    );
    window.visualViewport.addEventListener(
      "scroll",
      queueMiniCartViewportSync,
    );
  }

  if (phoneViewport.addEventListener) {
    phoneViewport.addEventListener("change", queueMiniCartViewportSync);
  } else {
    phoneViewport.addListener(queueMiniCartViewportSync);
  }

  syncMiniCartViewport();
});

// Let customers collapse the Afterpay instructions in either cart layout.
// Event delegation also covers cart markup refreshed by AJAX.
document.addEventListener("click", (event) => {
  const toggle = event.target.closest("[data-cart-promotion-toggle]");

  if (!toggle) return;

  const promotion = toggle.closest(".cart-promotion-progress");
  const isExpanded = toggle.getAttribute("aria-expanded") === "true";

  toggle.setAttribute("aria-expanded", String(!isExpanded));
  toggle.setAttribute(
    "aria-label",
    isExpanded ? "Show offer instructions" : "Hide offer instructions",
  );
  promotion?.classList.toggle("is-collapsed", isExpanded);
});

// Fix search results and nav-bar layer issue
const searchWrapper = document.querySelector(".header__search-bar-wrapper");
const wrapper = document.querySelector(".shopify-section__header");

if (searchWrapper && wrapper) {
  const observer = new MutationObserver(() => {
    if (searchWrapper.classList.contains("is-fixed")) {
      wrapper.classList.add("search-active");
    } else {
      wrapper.classList.remove("search-active");
    }
  });

  observer.observe(searchWrapper, {
    attributes: true,
    attributeFilter: ["class"],
  });
}

// Product label colour
document.querySelectorAll(".product-label").forEach((label) => {
  const text = label.textContent.trim().toLowerCase();

  if (text === "clearance") {
    label.classList.add("product-label--clearance");
  }

  if (text === "instructor pick") {
    label.classList.add("product-label--instructor-pick");
  }
});

// Keep cart promotion messages and Scala's special checkout button in sync with
// the promotion schedule configured in Theme settings.
document.addEventListener("DOMContentLoaded", () => {
  const promotionMessageSelector = ".cart-promotion-progress[data-cart-promotion-end-at]";
  const promotionControllerSelector = "[data-cart-promotion-controller]";
  const specialCheckoutSelector = "scala-cart-button";
  const maximumTimerDelay = 2147483647;
  let scheduleTimerId;
  let mutationSyncQueued = false;

  function hidePromotion(promotion) {
    promotion.hidden = true;

    const alertWrapper = promotion.closest(".mini-cart__alert-wrapper");

    if (
      alertWrapper &&
      !alertWrapper.querySelector(".alert, .cart-promotion-progress:not([hidden])")
    ) {
      alertWrapper.hidden = true;
    }
  }

  function parsePromotionTime(value) {
    const timestamp = Date.parse(value || "");

    return Number.isNaN(timestamp) ? null : timestamp;
  }

  function hideSpecialCheckout(host) {
    if (host.dataset.cartPromotionHiddenByTimer === "true") return;

    host.dataset.cartPromotionHiddenByTimer = "true";
    host.dataset.cartPromotionPreviousHidden = String(host.hidden);
    host.dataset.cartPromotionPreviousDisplay = host.style.getPropertyValue("display");
    host.dataset.cartPromotionPreviousDisplayPriority =
      host.style.getPropertyPriority("display");
    host.dataset.cartPromotionHadAriaHidden = String(
      host.hasAttribute("aria-hidden"),
    );
    host.dataset.cartPromotionPreviousAriaHidden =
      host.getAttribute("aria-hidden") || "";

    host.hidden = true;
    host.setAttribute("aria-hidden", "true");
    host.style.setProperty("display", "none", "important");
  }

  function restoreSpecialCheckout(host) {
    if (host.dataset.cartPromotionHiddenByTimer !== "true") return;

    host.hidden = host.dataset.cartPromotionPreviousHidden === "true";

    const previousDisplay = host.dataset.cartPromotionPreviousDisplay;
    const previousDisplayPriority =
      host.dataset.cartPromotionPreviousDisplayPriority;

    if (previousDisplay) {
      host.style.setProperty(
        "display",
        previousDisplay,
        previousDisplayPriority,
      );
    } else {
      host.style.removeProperty("display");
    }

    if (host.dataset.cartPromotionHadAriaHidden === "true") {
      host.setAttribute(
        "aria-hidden",
        host.dataset.cartPromotionPreviousAriaHidden,
      );
    } else {
      host.removeAttribute("aria-hidden");
    }

    delete host.dataset.cartPromotionHiddenByTimer;
    delete host.dataset.cartPromotionPreviousHidden;
    delete host.dataset.cartPromotionPreviousDisplay;
    delete host.dataset.cartPromotionPreviousDisplayPriority;
    delete host.dataset.cartPromotionHadAriaHidden;
    delete host.dataset.cartPromotionPreviousAriaHidden;
  }

  function syncSpecialCheckout(controller, now) {
    const promotionEnabled =
      controller.dataset.cartPromotionEnabled === "true";
    const startTime = parsePromotionTime(
      controller.dataset.cartPromotionStartAt,
    );
    const endTime = parsePromotionTime(controller.dataset.cartPromotionEndAt);
    const promotionIsActive =
      promotionEnabled &&
      (startTime === null || now >= startTime) &&
      (endTime === null || now < endTime);

    controller.querySelectorAll(specialCheckoutSelector).forEach((host) => {
      if (promotionIsActive) {
        restoreSpecialCheckout(host);
      } else {
        hideSpecialCheckout(host);
      }
    });

    if (!promotionEnabled) return [];

    return [startTime, endTime].filter(
      (timestamp) => timestamp !== null && timestamp > now,
    );
  }

  function syncPromotionSchedule() {
    window.clearTimeout(scheduleTimerId);

    const now = Date.now();
    let nextTransition = null;

    document.querySelectorAll(promotionMessageSelector).forEach((promotion) => {
      const endTime = parsePromotionTime(
        promotion.dataset.cartPromotionEndAt,
      );

      if (endTime === null) return;

      if (now >= endTime) {
        hidePromotion(promotion);
      } else if (nextTransition === null || endTime < nextTransition) {
        nextTransition = endTime;
      }
    });

    document.querySelectorAll(promotionControllerSelector).forEach((controller) => {
      syncSpecialCheckout(controller, now).forEach((timestamp) => {
        if (nextTransition === null || timestamp < nextTransition) {
          nextTransition = timestamp;
        }
      });
    });

    if (nextTransition !== null) {
      const delay = Math.min(
        Math.max(nextTransition - now + 100, 0),
        maximumTimerDelay,
      );

      scheduleTimerId = window.setTimeout(syncPromotionSchedule, delay);
    }
  }

  const cartObserver = new MutationObserver(() => {
    if (mutationSyncQueued) return;

    mutationSyncQueued = true;

    window.requestAnimationFrame(() => {
      mutationSyncQueued = false;
      syncPromotionSchedule();
    });
  });

  cartObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  document.addEventListener("cart:rerendered", syncPromotionSchedule, true);
  syncPromotionSchedule();
});

// Shared controller for both full-width and product-card promotion countdowns.
// The initial time comes from Shopify's server; performance.now() then keeps the
// countdown monotonic without trusting or repeatedly reading the shopper's clock.
(() => {
  const countdownSelector = "[data-countdown-root]";
  const countdownTimers = new WeakMap();

  function clearCountdown(root) {
    const timer = countdownTimers.get(root);

    if (timer) window.clearInterval(timer);
    countdownTimers.delete(root);
  }

  function initializeCountdown(root) {
    if (!root || root.dataset.countdownReady === "true") return;

    root.dataset.countdownReady = "true";

    const target = Date.parse(root.dataset.countdownTarget || "");
    const serverNowSeconds = Number(root.dataset.countdownServerNow);
    const performanceStart = window.performance.now();
    const serverStart = Number.isFinite(serverNowSeconds)
      ? serverNowSeconds * 1000
      : Date.now();
    const designMode = root.dataset.designMode === "true";
    const hideOnComplete = root.dataset.hideOnComplete === "true";
    const completeClass = root.dataset.countdownCompleteClass;
    const clock = root.querySelector("[data-countdown-clock]");
    const complete = root.querySelector("[data-countdown-complete]");
    const setup = root.querySelector("[data-countdown-setup]");
    const accessibleText = root.querySelector(
      "[data-countdown-accessible-text]",
    );
    const values = {
      days: root.querySelector("[data-countdown-days]"),
      hours: root.querySelector("[data-countdown-hours]"),
      minutes: root.querySelector("[data-countdown-minutes]"),
      seconds: root.querySelector("[data-countdown-seconds]"),
    };
    const currentTime = () =>
      serverStart + (window.performance.now() - performanceStart);

    function setValue(element, value) {
      if (element) element.textContent = String(value).padStart(2, "0");
    }

    function finishCountdown() {
      clearCountdown(root);

      if (completeClass) root.classList.add(completeClass);
      if (clock) clock.hidden = true;

      if (hideOnComplete && !designMode) {
        root.hidden = true;
        return;
      }

      if (complete) complete.hidden = false;
      if (accessibleText) {
        accessibleText.textContent = "The countdown is complete.";
      }
    }

    function updateCountdown() {
      if (!root.isConnected) {
        clearCountdown(root);
        return;
      }

      const remaining = target - currentTime();

      if (remaining <= 0) {
        finishCountdown();
        return;
      }

      const totalSeconds = Math.floor(remaining / 1000);

      setValue(values.days, Math.floor(totalSeconds / 86400));
      setValue(values.hours, Math.floor((totalSeconds % 86400) / 3600));
      setValue(values.minutes, Math.floor((totalSeconds % 3600) / 60));
      setValue(values.seconds, totalSeconds % 60);
    }

    if (!Number.isFinite(target)) {
      if (clock) clock.hidden = true;
      if (setup) setup.hidden = false;
      if (!designMode) root.hidden = true;
      return;
    }

    if (setup) setup.hidden = true;
    updateCountdown();

    if (target > currentTime()) {
      countdownTimers.set(root, window.setInterval(updateCountdown, 1000));
    }
  }

  function initializeCountdowns(scope = document) {
    if (scope.matches?.(countdownSelector)) initializeCountdown(scope);
    scope.querySelectorAll?.(countdownSelector).forEach(initializeCountdown);
  }

  function startCountdowns() {
    initializeCountdowns();

    const countdownObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) initializeCountdowns(node);
        });
      });
    });

    countdownObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startCountdowns, {
      once: true,
    });
  } else {
    startCountdowns();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initializeCountdowns(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll?.(countdownSelector).forEach(clearCountdown);
  });
})();
